import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DropdownListModule } from '@innovation-os-ng/ui/dropdown-list';
import { Ui2ButtonModule } from '@innovation-os-ng/ui/ui2-button';
import { TranslateModule, TranslateService } from '@itonics/ngx-translate-simple';
import { TrackedEvents } from '../feed-widget-v2/constants/tracked-events';
import { SortOption } from '../feed-widget-v2/interfaces/feed';

export enum SortOptionType {
  SUBMENU = 'submenu',
  MENU = 'menu'
}

export interface SortMenuOption {
  uri: string;
  label: string;
  type: string;
  direction?: string;
  icon?: string;
}

export const DEFAULT_SUB_OPTION: Array<SortMenuOption> = [
  {
    uri: 'asc',
    label: 'newsFeedWidget.sortList.ascending',
    type: SortOptionType.SUBMENU
  },
  {
    uri: 'desc',
    label: 'newsFeedWidget.sortList.descending',
    type: SortOptionType.SUBMENU
  }
];

export const DEFAULT_MENU_OPTION: Array<SortMenuOption> = [
  {
    uri: '_score',
    label: 'newsFeedWidget.sortList.relevance',
    type: SortOptionType.MENU,
    direction: 'desc'
  },
  {
    uri: 'publishedDate',
    label: 'newsFeedWidget.sortList.publishedDate',
    type: SortOptionType.MENU,
    direction: 'desc'
  }
];

@Component({
  selector: 'signals-sort-list',
  templateUrl: './sort-list.component.html',
  styleUrls: ['./sort-list.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, DropdownListModule, Ui2ButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SortListComponent implements OnInit {
  @Input()
  public menuOptions: Array<SortMenuOption>;
  @Input()
  public subMenuOptions: Array<SortMenuOption>;

  @Input()
  public menuOptionSelected: SortMenuOption;
  @Input()
  public subMenuOptionSelected: SortMenuOption;

  /**
   * Receives the input that state whether
   * to use Icons or label for sorting feature.
   *
   * Note: In insights we use icons and in signals feed
   * we use label.
   */
  @Input()
  public useLabel: boolean = false;

  @Output()
  public onSort = new EventEmitter<SortOption>();

  /**
   * Emit event name for tracking via ancestor / hosting app
   */
  @Output()
  public trackEvent: EventEmitter<string> = new EventEmitter<string>();

  public open: boolean = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService
  ) {}

  ngOnInit() {
    if (!this.subMenuOptions) {
      this.subMenuOptions = DEFAULT_SUB_OPTION;
    }
    if (!this.menuOptions) {
      this.menuOptions = DEFAULT_MENU_OPTION;
    }

    if (!this.menuOptionSelected && this.menuOptions && this.menuOptions.length) {
      this.menuOptionSelected = this.menuOptions[0];
    }

    if (!this.subMenuOptionSelected && this.subMenuOptions && this.subMenuOptions.length) {
      this.subMenuOptionSelected = this.subMenuOptions[1];
    }
    this.cdr.detectChanges();
    this.translate.get(this.menuOptions[0].label).subscribe((data) => {
      this.cdr.detectChanges();
    });
  }

  public selectOption(option: SortMenuOption) {
    if (option.type === SortOptionType.SUBMENU) {
      this.subMenuOptionSelected = option;
      this.trackEvent.emit(`${TrackedEvents.SORT_PREFIX}${option.uri === 'asc' ? 'Ascending' : 'Descending'}`);
    } else {
      this.subMenuOptionSelected = this.subMenuOptions.find((subOption) => subOption.uri === option.direction);
      this.menuOptionSelected = option;
      this.trackEvent.emit(`${TrackedEvents.SORT_PREFIX}${option.uri}`);
    }
    if (this.onSort) {
      this.onSort.emit({
        sortBy: this.menuOptionSelected.uri,
        sortOrder: this.subMenuOptionSelected.uri
      });
    }
  }

  /**
   * Make it possible to check the open state of the menu, this will only reflects the change and can not be used to open or close the menu
   *
   * @param isOpen
   */
  menuOpen(isOpen: boolean) {
    this.open = isOpen;
  }
}
